import { Component, OnInit, inject } from '@angular/core';
import { create } from '@bufbuild/protobuf';
import { TimestampSchema, timestampDate } from '@bufbuild/protobuf/wkt';
import { NbWindowService } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { filter, first, interval, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  fetchMyTimeTracking,
  selectMyTimeTracking,
  selectMyTimeTrackingInProgress,
  startMyTimeTrackingForSurvey,
  stopMyTimeTrackingForSurvey,
} from '../../../store/time-tracking';
import { GlobalTimeTrackingModalComponent } from '../time-tracking-modal/global-time-tracking-modal.component';
import { GlobalTimeTrackingOfficeModalComponent } from '../time-tracking-office-modal/global-time-tracking-office-modal.component';

@UntilDestroy()
@Component({
  selector: 'vmspdb-time-tracking-header',
  templateUrl: './global-time-tracking-header.component.html',
  styleUrls: ['./global-time-tracking-header.component.scss'],
  standalone: false,
})
export class GlobalTimeTrackingHeaderComponent implements OnInit {
  private store = inject(Store);
  private win = inject(NbWindowService);
  private actions$ = inject(Actions);

  inProgress$ = this.store.select(selectMyTimeTrackingInProgress);
  myTracking$ = this.store.select(selectMyTimeTracking);
  timer$ = this.myTracking$.pipe(
    filter((res) => !!res),
    switchMap((result) =>
      interval(1000).pipe(
        map(() =>
          intervalToDuration({
            start: result?.startTime
              ? timestampDate(create(TimestampSchema, result.startTime))
              : new Date(),
            end: new Date(),
          }),
        ),
      ),
    ),
    map(
      (interval) =>
        `${zeroPad(interval.hours ?? 0)}:${zeroPad(
          interval.minutes ?? 0,
        )}:${zeroPad(interval.seconds ?? 0)}`,
    ),
  );

  ngOnInit(): void {
    this.store.dispatch(fetchMyTimeTracking());

    this.actions$
      .pipe(
        untilDestroyed(this),
        ofType(startMyTimeTrackingForSurvey),
        tap(({ projectUuid, projectSurveyTaskUuid }) =>
          this.win.open(GlobalTimeTrackingModalComponent, {
            title: 'Start Time Tracking',
            windowClass: 'modal-compact',
            context: {
              allowProjectUpdate: false,
              model: { projectUuid, projectSurveyTaskUuid },
            },
          }),
        ),
      )
      .subscribe();

    this.actions$
      .pipe(
        untilDestroyed(this),
        ofType(stopMyTimeTrackingForSurvey),
        tap(() => this.stopTracking()),
      )
      .subscribe();
  }

  startTracking() {
    this.win.open(GlobalTimeTrackingModalComponent, {
      title: 'Start Time Tracking',
      windowClass: 'modal-compact',
    });
  }

  stopTracking() {
    this.myTracking$
      .pipe(
        first(),
        untilDestroyed(this),
        tap((model) => {
          const modal = model?.projectUuid
            ? GlobalTimeTrackingModalComponent
            : GlobalTimeTrackingOfficeModalComponent;
          this.win.open(modal, {
            title: 'Switch / Stop Time Tracking',
            windowClass: 'modal-compact',
            context: {
              model,
              switchingProjects: true,
              allowProjectUpdate: false,
            },
          });
        }),
      )
      .subscribe();
  }
}

function zeroPad(n: number) {
  return String(n).padStart(2, '0');
}
